import { Stack, Chip } from '@mui/material';
import {
  PredictionCategory,
  UNCATEGORIZED,
} from '../../Predictions/constants/categories';
import {
  SUBCATEGORIES_BY_CATEGORY,
  subcategoryLabel,
} from '../../Predictions/constants/subcategories';
import { PairStatsByCategory } from '../api/usePairMapper';

interface Props {
  category: PredictionCategory;
  value: string | null;
  onChange: (subcategory: string | null) => void;
  stats?: PairStatsByCategory;
}

export const SubcategoryChips = ({
  category,
  value,
  onChange,
  stats,
}: Props) => {
  // Uncategorized is a leaf bucket — pairs missing a category have no
  // meaningful subcategory, so the chip row would only show "All" anyway.
  // Don't render the row at all to keep the UI clean.
  if (category === UNCATEGORIZED) return null;

  const subcategories = SUBCATEGORIES_BY_CATEGORY[category] ?? [];
  const counts = stats?.byCategory?.[category]?.bySubcategory ?? {};

  // Surface any subcategory present in the data that isn't in the canonical
  // list — keeps drift from the matching-core taxonomy visible instead of
  // silently dropping those rows.
  const allSubs = Array.from(
    new Set([...subcategories, ...Object.keys(counts)]),
  );

  if (allSubs.length === 0) return null;

  return (
    <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
      <Chip
        label={`All (${stats?.byCategory?.[category]?.total ?? 0})`}
        size="small"
        color={value === null ? 'primary' : 'default'}
        variant={value === null ? 'filled' : 'outlined'}
        onClick={() => onChange(null)}
      />
      {allSubs.map((sub) => {
        const count = counts[sub] ?? 0;
        const active = value === sub;
        return (
          <Chip
            key={sub}
            label={`${subcategoryLabel(sub)} (${count})`}
            size="small"
            color={active ? 'primary' : 'default'}
            variant={active ? 'filled' : 'outlined'}
            onClick={() => onChange(active ? null : sub)}
          />
        );
      })}
    </Stack>
  );
};
